
/* Sockable conditional progress bar — no changes to Shopify shipping rates.
 * Priority: Fundraising Sock Pack quantities (10+) override dollar message.
 * Regular socks + sample packs contribute to $50, except sample-only carts show nothing.
 */
(() => {
  const THRESHOLD_CENTS = 5000;
  const PACK_TARGET = 10;
  const BAR_SELECTOR = '[data-sockable-shipping-progress]';
  let lastCart = null;
  let refreshVersion = 0;
  let refreshTimer = null;

  const money = (cents) => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2
  }).format(cents / 100);

  function shippingState(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    let packCount = 0;
    let eligibleCents = 0;
    let hasSingleSock = false;
    let hasSample = false;
    let unknown = false;
    for (const item of items) {
      const type = String(item.product_type || '').trim().toLowerCase();
      const quantity = Number(item.quantity) || 0;
      if (type === 'sock pack') {
        packCount += quantity;
      } else if (type === 'single sock') {
        hasSingleSock = true;
        eligibleCents += Number(item.final_line_price ?? item.line_price ?? (item.price * quantity)) || 0;
      } else if (type === 'sample pack') {
        hasSample = true;
        eligibleCents += Number(item.final_line_price ?? item.line_price ?? (item.price * quantity)) || 0;
      } else {
        unknown = true;
      }
    }
    if (packCount > 0) {
      const left = Math.max(0, PACK_TARGET - packCount);
      return {
        show: true,
        mode: 'packs',
        complete: left === 0,
        progress: Math.min(100, packCount / PACK_TARGET * 100),
        message: left === 0
          ? "You've unlocked FREE shipping with your fundraising sock packs!"
          : "Add " + left + " more fundraising sock pack" + (left === 1 ? "" : "s") + " for FREE shipping!"
      };
    }
    // Sample packs currently ship free by themselves, so don't show a misleading message.
    // Unrecognised product types are excluded from eligibility and don't trigger the bar.
    if (!hasSingleSock && (hasSample || unknown)) return { show: false };
    if (!hasSingleSock && items.length === 0) {
      return { show: true, empty: true, complete: false, progress: 0, message: "FREE shipping when you spend $50 on socks!" };
    }
    const left = Math.max(0, THRESHOLD_CENTS - eligibleCents);
    return {
      show: true,
      complete: left === 0,
      progress: Math.min(100, eligibleCents / THRESHOLD_CENTS * 100),
      message: left === 0
        ? "You've unlocked FREE shipping on your socks!"
        : "You're " + money(left) + " away from FREE shipping!"
    };
  }

  function applyState(state) {
    document.querySelectorAll(BAR_SELECTOR).forEach(bar => {
      // Collection-page invitation follows the collection being browsed.
      // The cart drawer and cart page always follow actual cart contents.
      // Sample-only carts remain hidden; carts with packs show their actual quantity progress.
      const isFundraisingPackCollection =
        bar.getAttribute('data-sockable-shipping-progress') === 'header' &&
        (bar.getAttribute('data-shipping-context') || '').includes('fundraising-sock-packs');
      const displayState = (isFundraisingPackCollection && state.show && state.mode !== 'packs')
        ? {
            show: true,
            empty: state.empty,
            complete: false,
            progress: 0,
            message: 'FREE shipping when you order 10 Fundraising Sock Packs!'
          }
        : state;
      // On a completely empty cart show the invitation only on collection pages.
      const isEmptyCartSurface = displayState.empty && bar.getAttribute('data-sockable-shipping-progress') !== 'header';
      bar.hidden = !displayState.show || !!isEmptyCartSurface;
      if (bar.hidden) return;
      const message = bar.querySelector('[data-shipping-message]');
      const note = bar.querySelector('[data-shipping-note]');
      if (note) {
        const isPackOffer = displayState.mode === 'packs' || isFundraisingPackCollection;
        const noteText = displayState.complete ? '' : isPackOffer
          ? '$10.95 flat-rate shipping for 1–9 packs'
          : 'Flat-rate shipping $5.95 on sock orders under $50';
        note.hidden = !noteText;
        if (noteText && note.textContent !== noteText) note.textContent = noteText;
      }
      const fill = bar.querySelector('[data-shipping-fill]');
      const track = bar.querySelector('[data-shipping-track]');
      if (message && message.textContent !== displayState.message) message.textContent = displayState.message;
      if (fill) fill.style.width = displayState.progress + '%';
      if (track) track.setAttribute('aria-valuenow', String(Math.round(displayState.progress)));
      bar.classList.toggle('sockable-shipping-progress--complete', displayState.complete);
    });
  }

  function addDrawerProgressBar() {
    // Sits directly under "Your cart", before the Product / Total headings.
    // The bar lives outside cart-drawer-items so quantity changes can redraw
    // the items without disrupting the header or the shipping progress.
    const drawer = document.querySelector('cart-drawer');
    const header = drawer?.querySelector('.drawer__inner > .drawer__header');
    if (!header) return;
    const existing = drawer.querySelector('[data-sockable-shipping-progress="drawer"]');
    if (existing) {
      if (existing.previousElementSibling !== header) header.insertAdjacentElement('afterend', existing);
      return;
    }
    const bar = document.createElement('div');
    bar.className = 'sockable-shipping-progress sockable-shipping-progress--drawer';
    bar.setAttribute('data-sockable-shipping-progress', 'drawer');
    bar.setAttribute('aria-live', 'polite');
    bar.hidden = true;
    bar.innerHTML =
      '<div class="sockable-shipping-progress__inner">' +
      '<div class="sockable-shipping-progress__copy" data-shipping-copy>' +
      '<span class="sockable-shipping-progress__message" data-shipping-message></span>' +
      '<span class="sockable-shipping-progress__note" data-shipping-note hidden></span></div>' +
      '<div class="sockable-shipping-progress__track" role="progressbar" aria-label="Free-shipping progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" data-shipping-track>' +
      '<span class="sockable-shipping-progress__fill" data-shipping-fill></span></div></div>';
    header.insertAdjacentElement('afterend', bar);
    if (lastCart) applyState(shippingState(lastCart));
  }


  function addCartPageProgressBar() {
    // The full /cart page is separate from Dawn's slide-out cart drawer.
    const container = document.querySelector('main cart-items .page-width');
    if (!container || container.querySelector('[data-sockable-shipping-progress="cart-page"]')) return;
    const bar = document.createElement('div');
    bar.className = 'sockable-shipping-progress sockable-shipping-progress--cart-page';
    bar.setAttribute('data-sockable-shipping-progress', 'cart-page');
    bar.setAttribute('aria-live', 'polite');
    bar.hidden = true;
    bar.innerHTML =
      '<div class="sockable-shipping-progress__inner">' +
      '<div class="sockable-shipping-progress__copy" data-shipping-copy>' +
      '<span class="sockable-shipping-progress__message" data-shipping-message></span>' +
      '<span class="sockable-shipping-progress__note" data-shipping-note hidden></span></div>' +
      '<div class="sockable-shipping-progress__track" role="progressbar" aria-label="Free-shipping progress" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" data-shipping-track>' +
      '<span class="sockable-shipping-progress__fill" data-shipping-fill></span></div></div>';
    container.insertBefore(bar, container.firstChild);
    if (lastCart) applyState(shippingState(lastCart));
  }

  async function refresh() {
    const version = ++refreshVersion;
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(base + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Cart fetch failed');
      const cart = await response.json();
      if (version !== refreshVersion) return;
      lastCart = cart;
      addDrawerProgressBar();
      addCartPageProgressBar();
      applyState(shippingState(cart));
    } catch (e) {
      // Never show incorrect shipping claims when cart state cannot be verified.
      document.querySelectorAll(BAR_SELECTOR).forEach(bar => { bar.hidden = true; });
    }
  }
  function queueRefresh() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 75);
  }


  function positionCollectionShippingBar() {
    // Change position only: the same bar is moved, not duplicated.
    // No cart, fundraiser, or shipping-rate calculations are modified.
    const bar = document.querySelector('[data-sockable-shipping-progress="header"]');
    const main = document.getElementById('MainContent');
    if (!bar || !main) return;

    const sections = Array.from(main.children).filter(child =>
      child.classList && child.classList.contains('shopify-section')
    );
    const grid = sections.find(section =>
      section.id.includes('__product-grid') || !!section.querySelector('#ProductGridContainer')
    );
    const beforeGrid = grid ? sections.slice(0, sections.indexOf(grid)) : sections;

    // Collection templates differ. Use their first visible image/collection hero.
    // If no hero exists, put the message immediately before the product grid.
    const hero = beforeGrid.find(section =>
      section.querySelector('.banner__media, .collection-hero')
    );
    if (hero) {
      hero.insertAdjacentElement('afterend', bar);
    } else if (grid) {
      grid.insertAdjacentElement('beforebegin', bar);
    } else if (sections.length) {
      sections[0].insertAdjacentElement('afterend', bar);
    } else {
      main.prepend(bar);
    }
  }

  function init() {
    positionCollectionShippingBar();
    addDrawerProgressBar();
    addCartPageProgressBar();
    refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, queueRefresh);
    }
    document.addEventListener('shopify:cart:lines-update', queueRefresh);
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(() => addDrawerProgressBar())
        .observe(drawer, { childList: true, subtree: true });
    }
    document.addEventListener('click', (e) => {
      if (e.target.closest('#cart-icon-bubble')) queueRefresh();
    });
    window.addEventListener('pageshow', queueRefresh);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();